import { clampBoardName } from '../lib/text'
import Select from '../ui/Select'
import { useEffect, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { createBoard, importBoard, importIntoBoard, listBoards, type BoardListItem, type ImportReport } from '../api/client'
import { parseMondayXlsx } from '../board/mondayImport'
import type { ShellContext } from '../layout/AppShell'

/**
 * 首页（多看板列表页）—— M10 的 JSON 导入入口放在这里。
 *
 * 流程：选文件 → 前端先校验结构 → 选「新建看板 / 合并到现有看板」→ 调后端 → 显示导入报告。
 * 看板/分组/列/条目全部由后端落库；这里只保留"选中的文件内容"这种临时状态。
 */
export default function HomePage() {
  const { health, online } = useOutletContext<ShellContext>()
  const [boards, setBoards] = useState<BoardListItem[]>([])
  const [payload, setPayload] = useState<unknown>(null)
  const [fileName, setFileName] = useState('')
  const [error, setError] = useState('')
  const [mode, setMode] = useState<'new' | 'merge'>('new')
  const [targetId, setTargetId] = useState<number>(0)
  const [busy, setBusy] = useState(false)
  const [report, setReport] = useState<ImportReport | null>(null)
  // M11：列表页正式化 —— 搜索、排序、新建
  const [query, setQuery] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'id'>('id')
  const [newOpen, setNewOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  /** 导入前指定的新看板名（默认文件名；留空则用文件里带的看板名） */
  const [importName, setImportName] = useState("")
  /** monday 导出里疑似「进度」的列（导入前让用户逐列选：进度 / 数字） */
  const [progressCols, setProgressCols] = useState<{ key: string; title: string; sample: string }[]>([])
  const [asProgress, setAsProgress] = useState<Record<string, boolean>>({})
  /** 文件来源：friday = 我们自己导出的 XLSX，monday = monday.com 导出 */
  const [pkgSource, setPkgSource] = useState<"friday" | "monday" | "">("")
  /** 点「开始导入」时，如果检测到疑似进度列，先弹窗确认 */
  const [progressOpen, setProgressOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    void (async () => {
      try {
        const res = await listBoards()
        const list = res.boards ?? []
        setBoards(list)
        const first = list[0]
        if (first !== undefined) setTargetId(first.id)
      } catch {
        /* 列表拉不到不影响"新建"路径 */
      }
    })()
  }, [])

  const onPick = async (f: File | null) => {
    setReport(null)
    setError('')
    if (f === null) return
    // 默认用文件名做新看板名（用户可改）
    setImportName(f.name.replace(/\.(json|xlsx)$/i, ''))
    setFileName(f.name)
    try {
      if (/\.xlsx$/i.test(f.name)) {
        // monday.com 导出的 xlsx：前端解析成同一套 payload
        const parsedXlsx = await parseMondayXlsx(f)
        const { payload: mpayload, warnings } = parsedXlsx
        setProgressCols(parsedXlsx.progressCandidates ?? [])
        setAsProgress({})
        setPkgSource(parsedXlsx.source ?? "monday")
        setPayload(mpayload)
        setFileName(f.name)
        setError(warnings.length > 0 ? warnings.join("；") : "")
        return
      }
      const text = await f.text()
      setProgressCols([])
      setPkgSource("")
      const parsed = JSON.parse(text) as { version?: unknown; columns?: unknown; groups?: unknown; items?: unknown } | null
      if (parsed === null || typeof parsed !== 'object') throw new Error('内容不是 JSON 对象')
      if (parsed.version !== 1) throw new Error(`不支持的 version：${String(parsed.version)}（只认 1）`)
      if (!Array.isArray(parsed.columns) || !Array.isArray(parsed.groups) || !Array.isArray(parsed.items)) {
        throw new Error('缺少 columns / groups / items 数组')
      }
      setPayload(parsed)
    } catch (e) {
      setPayload(null)
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  /** 导入前如果填了看板名，就覆盖 payload 里的 board.name */
  /** 按「进度/数字」的选择改写 payload：选了进度的列改成进度类型，并丢弃数值 */
  const applyProgressChoice = (p: unknown) => {
    if (progressCols.length === 0 || p === null || typeof p !== "object") return p
    const obj = p as { columns?: { key: string; type: string }[]; items?: { values?: Record<string, string> }[] }
    const chosen = progressCols.filter((c) => asProgress[c.key] === true).map((c) => c.key)
    if (chosen.length === 0) return p
    return {
      ...obj,
      columns: (obj.columns ?? []).map((c) => (chosen.includes(c.key) ? { ...c, type: "columns-battery" } : c)),
      items: (obj.items ?? []).map((it) => {
        if (it.values === undefined) return it
        const values = { ...it.values }
        for (const k of chosen) delete values[k]
        return { ...it, values }
      }),
    }
  }

  const withImportName = (p: unknown) => {
    const name = clampBoardName(importName.trim())
    if (name === "") return p
    if (p === null || typeof p !== "object") return p
    return { ...(p as Record<string, unknown>), board: { name } }
  }

  const doImport = async (progressConfirmed = false) => {
    if (!progressConfirmed && progressCols.length > 0) {
      setProgressOpen(true)
      return
    }
    if (payload === null) return
    setBusy(true)
    setError('')
    try {
      const res = mode === 'new' ? await importBoard(applyProgressChoice(withImportName(payload))) : await importIntoBoard(targetId, payload)
      setReport(res)
      setPayload(null)
      setFileName('')
      if (fileRef.current !== null) fileRef.current.value = ''
      const list = await listBoards()
      setBoards(list.boards ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const visible = boards
    .filter((b) => (query === '' ? true : b.name.toLowerCase().includes(query.toLowerCase())))
    .sort((a, b) => (sortBy === 'name' ? a.name.localeCompare(b.name, 'zh') : a.id - b.id))

  const doCreate = async () => {
    if (creating) return
    setCreating(true)
    try {
      const res = await createBoard(clampBoardName(newName.trim()))
      setNewOpen(false)
      setNewName('')
      window.location.href = `/boards/${res.boardId}`
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="page">
      <div className="page__head">
        <h1 className="page__title">看板</h1>
          <button type="button" className="dialog__btn" onClick={() => setNewOpen(true)}>
            + 新建看板
          </button>
      </div>

      <section className="card">
        <h2 className="card__title">全部看板（{boards.length}）</h2>
          <div className="board-tools">
            <input
              className="board-tools__search"
              type="search"
              placeholder="搜索看板名…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Select
              className="board-tools__sort"
              value={sortBy}
              onChange={(v) => setSortBy(v === 'name' ? 'name' : 'id')}
            >
              <option value="id">按编号</option>
              <option value="name">按名称</option>
            </Select>
            {query !== '' && (
              <span className="dim">
                {visible.length} / {boards.length}
              </span>
            )}
          </div>
          {boards.length === 0 && <p className="card__hint">还没有看板。可以点右上「+ 新建看板」，或导入一个 JSON。</p>}
          {boards.length > 0 && visible.length === 0 && (<p className="card__hint">没有匹配「{query}」的看板。</p>)}
        <div className="board-cards">
          {visible.map((b) => (
            <a key={b.id} className="board-card" href={`/boards/${b.id}`}>
              <span className="board-card__name">{b.name}</span>
              <span className="board-card__id">#{b.id}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="card">
        <h2 className="card__title">导入看板（JSON / monday XLSX）</h2>
        <p className="card__hint">
          导入自己导出的 JSON（看板右上 ⋯ → 导出 → JSON）。新建：按文件里的看板名建新看板；
          合并：同名分组并进已有分组、同 ColumnKey 复用现有列、条目全部追加（不覆盖现有数据）。
        </p>

        <div className="import-row">
          {/* 隐藏原生 input，用统一风格的按钮触发（与看板页/弹窗一致） */}
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={(e) => void onPick(e.target.files?.[0] ?? null)}
          />
          <button type="button" className="dialog__btn" onClick={() => fileRef.current?.click()}>
            选择文件（JSON / XLSX）
          </button>
          <span className="import-file">{fileName !== '' ? fileName : '未选择文件'}</span>
        </div>

          {pkgSource !== "" && (
            <p className="import-source">
              来源：{pkgSource === "friday" ? "Friday 导出的 XLSX（列类型精确还原）" : "monday.com 导出的 XLSX（按特征推断）"}
            </p>
          )}

        {error !== '' && <p className="import-error">{error}</p>}

        {payload !== null && (
          <div className="import-mode">
            <label className="import-radio">
              <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} />
              新建看板
            </label>
              {mode === 'new' && (
                <label className="import-name">
                  新看板名
                  <input
                    className="import-name__input"
                    type="text"
                    maxLength={40}
                    placeholder={fileName !== '' ? fileName.replace(/\.(json|xlsx)$/i, '') : '留空 = 用文件里的看板名'}
                    value={importName}
                    onChange={(e) => setImportName(clampBoardName(e.target.value))}
                    onPaste={(e) => {
                      e.preventDefault()
                      const text = e.clipboardData.getData('text')
                      setImportName(clampBoardName(importName + text))
                    }}
                    onCompositionEnd={(e) => {
                      setImportName(clampBoardName((e.target as HTMLInputElement).value))
                    }}
                  />
                </label>
              )}

            <label className="import-radio">
              <input type="radio" checked={mode === 'merge'} onChange={() => setMode('merge')} />
              合并到现有看板
            </label>
            {mode === 'merge' && (
              <Select
                className="import-select"
                value={targetId}
                onChange={(v) => setTargetId(Number(v))}
              >
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}（#{b.id}）
                  </option>
                ))}
              </Select>
            )}
            <button
              type="button"
              className="dialog__btn dialog__btn--primary"
              disabled={busy}
              onClick={() => void doImport()}
            >
              {busy ? '导入中…' : '开始导入'}
            </button>
          </div>
        )}

        {report !== null && report.ok && (
          <div className="import-report">
            <div>已导入到《{report.boardName}》#{report.boardId}</div>
            <div>
              分组：新建 {report.groupsCreated ?? 0} / 复用 {report.groupsReused ?? 0}　列：新建{' '}
              {report.columnsCreated ?? 0} / 复用 {report.columnsReused ?? 0}　条目：
              {report.itemsCreated ?? 0}　跳过值：{report.valuesSkipped ?? 0}
            </div>
            {report.boardId !== undefined && (
              <div>
                <a className="page__link" href={`/boards/${report.boardId}`}>
                  打开看板 #{report.boardId} →
                </a>
              </div>
            )}
          </div>
        )}
        {report !== null && !report.ok && <p className="import-error">导入失败：{report.message ?? ''}</p>}

        <h3 className="card__sub">运行信息</h3>

        {online === false && <pre className="kv kv--err">服务端未连接</pre>}

        {health !== null && (
          <dl className="kv">
            <div className="kv__row">
              <dt>service</dt>
              <dd>{health.service}</dd>
            </div>
            <div className="kv__row">
              <dt>version</dt>
              <dd>{health.version}</dd>
            </div>
            <div className="kv__row">
              <dt>schema</dt>
              <dd>{health.schema_version}</dd>
            </div>
            <div className="kv__row">
              <dt>server time</dt>
              <dd>{new Date(health.time * 1000).toLocaleString()}</dd>
            </div>
          </dl>
        )}
      </section>
      {newOpen && (
        <div
          className="dialog-mask"
          onClick={(e) => { if (e.target === e.currentTarget) setNewOpen(false) }}
        >
          <div className="dialog" role="dialog" aria-modal="true" aria-label="新建看板">
            <header className="dialog__head">
              <span className="dialog__title">新建看板</span>
            </header>
            <div className="dialog__body">
              <input
                className="dialog__input"
                placeholder="看板名称（最多 40 个字）"
                maxLength={40}
                value={newName}
                autoFocus
                onChange={(e) => setNewName(clampBoardName(e.target.value))}
                onPaste={(e) => {
                  // 粘贴：先按纯文本截断再插入，避免 maxLength 之外的路径漏进来
                  e.preventDefault()
                  const text = e.clipboardData.getData('text')
                  setNewName(clampBoardName(newName + text))
                }}
                onCompositionEnd={(e) => {
                  // 输入法一次性上屏：合成结束后再截一次
                  setNewName(clampBoardName((e.target as HTMLInputElement).value))
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') void doCreate() }}
              />
              <div className="dialog__count">
                <span className={newName.length >= 40 ? 'dialog__count--max' : ''}>{newName.length}</span> / 40
              </div>
              <p className="dialog__note">重名会自动加后缀 (2)/(3)…；留空则用「未命名看板」。</p>
            </div>
            <footer className="dialog__foot">
              <button type="button" className="dialog__btn" onClick={() => setNewOpen(false)} disabled={creating}>
                取消
              </button>
              <button
                type="button"
                className="dialog__btn dialog__btn--primary"
                onClick={() => void doCreate()}
                disabled={creating}
              >
                创建
              </button>
            </footer>
          </div>
        </div>
      )}
      {progressOpen && (
        <div className="dialog-mask" onClick={(e) => { if (e.target === e.currentTarget) setProgressOpen(false) }}>
          <div className="dialog" role="dialog" aria-modal="true" aria-label="确认进度列">
            <header className="dialog__head">
              <span className="dialog__title">检测到疑似「进度」列</span>
            </header>
            <div className="dialog__body">
              <p className="dialog__note">
                勾选的列会导入成「进度」列（**忽略原数值**）；不勾则按数字导入。
              </p>
              <div className="progress-pick">
                {progressCols.map((c) => (
                  <label key={c.key} className="progress-pick__row">
                    <input
                      type="checkbox"
                      checked={asProgress[c.key] === true}
                      onChange={(e) => setAsProgress((prev) => ({ ...prev, [c.key]: e.target.checked }))}
                    />
                    <span className="progress-pick__name">{c.title}</span>
                    <span className="progress-pick__sample">（示例：{c.sample}）</span>
                    <span className="progress-pick__hint">{asProgress[c.key] === true ? '导入为进度' : '导入为数字'}</span>
                  </label>
                ))}
              </div>
            </div>
            <footer className="dialog__foot">
              <button type="button" className="dialog__btn" onClick={() => setProgressOpen(false)}>取消</button>
              <button
                type="button"
                className="dialog__btn dialog__btn--primary"
                onClick={() => { setProgressOpen(false); void doImport(true) }}
              >
                确认并导入
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  )
}
